import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { UsuarioLoginInterface } from '../interfaces/UsuarioLoginInterface';
import axios from 'axios';
import { API } from '../global/global';

type AuthContextProps = {
  token: string | null;
  usuario: UsuarioLoginInterface | null;
  rol: string | null;
  movimientoExternoId?: string;
  sistemaId?: string;
  isLoggedIn: boolean;
  refreshToken: string | null;
  tokenExpiration: string | null;
  login: (
    usuario: UsuarioLoginInterface | null,
    token: string | null,
    rol: string | null,
    movimiento_externo_id?: string,
    sistema_id?: string,
    refresh_token?: string,
    expires_in?: number
  ) => void;
  logout: () => void;
  getValidToken: () => Promise<string | null>;
  refreshAccessToken: () => Promise<string | null>;
};

export const AuthContext = React.createContext({} as AuthContextProps);

const getInitialState = () => ({
  token: localStorage.getItem('token'),
  refreshToken: localStorage.getItem('refresh_token'),
  tokenExpiration: localStorage.getItem('token_expiration'),
  rol: localStorage.getItem('rol'),
  usuario: localStorage.getItem('usuario') 
    ? JSON.parse(localStorage.getItem('usuario') || '{}')
    : null,
  movimientoExternoId: localStorage.getItem('movimientoExternoId') || undefined,
  sistemaId: localStorage.getItem('sistemaId') || undefined,
});

export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState(getInitialState);
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);

  const userIsLoggedIn = !!authState.token;

  const logoutHandler = useCallback(() => {
    setAuthState({
      token: null,
      refreshToken: null,
      tokenExpiration: null,
      usuario: null,
      rol: null,
      movimientoExternoId: undefined,
      sistemaId: undefined,
    });
    
    // Limpiar todo el localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiration');
    localStorage.removeItem('usuario');
    localStorage.removeItem('rol');
    localStorage.removeItem('movimientoExternoId');
    localStorage.removeItem('sistemaId');
  }, []);

  const loginHandler = useCallback((
    usuario: UsuarioLoginInterface | null,
    token: string | null,
    rol: string | null,
    movimiento_externo_id?: string,
    sistema_id?: string,
    refresh_token?: string,
    expires_in?: number
  ) => {
    // Calcular tiempo de expiración si se proporciona expires_in
    let tokenExpiration: string | null = null;
    if (expires_in) {
      tokenExpiration = (new Date().getTime() + (expires_in * 1000)).toString();
    }

    setAuthState({
      token,
      refreshToken: refresh_token || null,
      tokenExpiration,
      usuario,
      rol,
      movimientoExternoId: movimiento_externo_id,
      sistemaId: sistema_id,
    });

    // Guardar en localStorage
    if (token) localStorage.setItem('token', token);
    if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
    if (tokenExpiration) localStorage.setItem('token_expiration', tokenExpiration);
    if (rol) localStorage.setItem('rol', rol);
    if (usuario) localStorage.setItem('usuario', JSON.stringify(usuario));
    if (movimiento_externo_id) localStorage.setItem('movimientoExternoId', movimiento_externo_id);
    if (sistema_id) localStorage.setItem('sistemaId', sistema_id);
  }, []);

  const refreshAccessTokenHandler = useCallback(async (): Promise<string | null> => {
    // Si ya hay un refresh en proceso, esperar a que termine
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const performRefresh = async () => {
      const refreshToken = authState.refreshToken || localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        logoutHandler();
        return null;
      }

      try {
        const response = await axios.post(`${API}/refresh`, {
          refresh_token: refreshToken,
        });

        const { access_token, expires_in } = response.data.data;
        
        // Calcular nueva expiración
        const newTokenExpiration = (new Date().getTime() + (expires_in * 1000)).toString();
        
        // Actualizar estado y localStorage
        setAuthState(prev => ({
          ...prev,
          token: access_token,
          tokenExpiration: newTokenExpiration,
        }));
        
        localStorage.setItem('token', access_token);
        localStorage.setItem('token_expiration', newTokenExpiration);
        
        return access_token;
      } catch (error) {
        console.error('Error refreshing token:', error);
        logoutHandler();
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    };

    refreshPromiseRef.current = performRefresh();
    return refreshPromiseRef.current;
  }, [authState.refreshToken, logoutHandler]);

  const getValidTokenHandler = useCallback(async (): Promise<string | null> => {
    const token = authState.token;
    const tokenExpiration = authState.tokenExpiration;
    
    if (!token || !tokenExpiration) return null;

    // Verificar si el token está por expirar (5 minutos antes)
    const now = new Date().getTime();
    const expirationTime = parseInt(tokenExpiration);
    const fiveMinutes = 5 * 60 * 1000;

    if (now >= expirationTime - fiveMinutes) {
      // Token está por expirar, refrescarlo
      return await refreshAccessTokenHandler();
    }

    return token;
  }, [authState.token, authState.tokenExpiration, refreshAccessTokenHandler]);

  // Configurar interceptores de axios cuando se monta el componente
  useEffect(() => {
    // Request interceptor
    const requestInterceptor = axios.interceptors.request.use(
      async (config) => {
        // Solo agregar token si no es una petición de login o refresh
        if (!config.url?.includes('/login') && !config.url?.includes('/refresh')) {
          const token = await getValidTokenHandler();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/login') &&
          !originalRequest.url?.includes('/refresh') &&
          !originalRequest.url?.includes('/loginTemporal')
        ) {
          originalRequest._retry = true;

          try {
            const newToken = await refreshAccessTokenHandler();
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    // Cleanup
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [getValidTokenHandler, refreshAccessTokenHandler]);

  const contextValue = useMemo(() => ({
    token: authState.token,
    refreshToken: authState.refreshToken,
    tokenExpiration: authState.tokenExpiration,
    rol: authState.rol,
    usuario: authState.usuario,
    movimientoExternoId: authState.movimientoExternoId,
    sistemaId: authState.sistemaId,
    isLoggedIn: userIsLoggedIn,
    login: loginHandler,
    logout: logoutHandler,
    getValidToken: getValidTokenHandler,
    refreshAccessToken: refreshAccessTokenHandler,
  }), [authState, loginHandler, logoutHandler, getValidTokenHandler, refreshAccessTokenHandler, userIsLoggedIn]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};